// components/DiagramViewer.tsx
// Modal wrapper for DentalChart / BodyMap.
// Layout: chart on the left, annotation panel on the right (always visible).
import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, Button, Input, Space, Typography, Tag, Divider,
  message, Spin, Badge
} from 'antd';
import {
  SaveOutlined, DeleteOutlined, AimOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import axios from '../offline/profileApi';
import { isProfileQueued } from '../offline/profileActions';
import API from '../config/api';
import DentalChart from './DentalChart';
import BodyMap from './BodyMap';

const { Text } = Typography;

export type DiagramType = 'dental' | 'body';
export type AnnotationStatus = 'normal' | 'problem' | 'treated' | 'missing';

export interface DiagramAnnotation {
  status: AnnotationStatus;
  note: string;
  color: string;
}

export interface DiagramData {
  type: DiagramType;
  annotations: Record<string, DiagramAnnotation>;
}

interface DiagramViewerProps {
  visible: boolean;
  onClose: () => void;
  diagramType: DiagramType;
  patientId: string;
  doctorId: string;
  visitId: string;
  initialData?: DiagramData | null;
  onSaved?: (data: DiagramData) => void;
}

const STATUS_OPTIONS: { value: AnnotationStatus; label: string; color: string; bg: string }[] = [
  { value: 'problem', label: 'Problem',        color: '#ff4d4f', bg: '#fff1f0' },
  { value: 'treated', label: 'Treated',        color: '#52c41a', bg: '#f6ffed' },
  { value: 'missing', label: 'Missing/Absent', color: '#8c8c8c', bg: '#f5f5f5' },
  { value: 'normal',  label: 'Normal (clear)', color: '#d0d5dd', bg: '#ffffff' },
];

const STATUS_COLOR_MAP: Record<AnnotationStatus, string> = {
  normal: '#d0d5dd',
  problem: '#ff4d4f',
  treated: '#52c41a',
  missing: '#8c8c8c',
};

const getRegionLabel = (diagramType: DiagramType, regionId: string): string => {
  if (diagramType === 'dental') return `Tooth ${regionId}`;
  return regionId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const DiagramViewer: React.FC<DiagramViewerProps> = ({
  visible, onClose, diagramType, patientId, doctorId, visitId, initialData, onSaved,
}) => {
  const [annotations, setAnnotations] = useState<Record<string, DiagramAnnotation>>({});
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      setAnnotations(initialData?.annotations ?? {});
      setSelectedRegion(null);
    }
  }, [visible, initialData]);

  const handleRegionClick = useCallback((regionId: string) => {
    setSelectedRegion(regionId);
  }, []);

  const handleAnnotationChange = useCallback((
    regionId: string,
    patch: Partial<DiagramAnnotation> | null
  ) => {
    setAnnotations(prev => {
      const next = { ...prev };
      if (patch === null || patch.status === 'normal') {
        delete next[regionId];
      } else {
        const mergedStatus = patch.status ?? prev[regionId]?.status ?? 'problem';
        next[regionId] = {
          status: prev[regionId]?.status ?? 'problem',
          note: prev[regionId]?.note ?? '',
          ...patch,
          color: STATUS_COLOR_MAP[mergedStatus],
        };
      }
      return next;
    });
  }, []);

  const clearRegion = (regionId: string) => {
    setAnnotations(prev => {
      const next = { ...prev };
      delete next[regionId];
      return next;
    });
    if (selectedRegion === regionId) setSelectedRegion(null);
  };

  const handleSave = async () => {
    if (!patientId || !doctorId || !visitId) {
      message.error('Missing patient or visit information.');
      return;
    }
    setSaving(true);
    try {
      const diagramData: DiagramData = { type: diagramType, annotations };
      await axios.put(
        `${API.BASE_URL}/api/patients/${patientId}/doctor/${doctorId}/visits/${visitId}`,
        { diagram_data: diagramData }
      );
      message.success('Diagram saved successfully');
      onSaved?.(diagramData);
      onClose();
    } catch (err) {
      if (isProfileQueued(err)) { message.warning('Saved on this device — pending sync.'); onClose(); return; }
      console.error('Error saving diagram:', err);
      message.error('Failed to save diagram. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const annotatedCount = Object.keys(annotations).length;
  const isDental = diagramType === 'dental';
  const currentAnn = selectedRegion ? annotations[selectedRegion] : undefined;

  // ── Annotation panel (right side) ─────────────────────────────
  const annotationPanel = (
    <div style={{
      width: 260, flexShrink: 0,
      display: 'flex', flexDirection: 'column', gap: 12,
      height: '100%', overflowY: 'auto',
    }}>
      {/* Selected region */}
      {selectedRegion ? (
        <div style={{
          background: '#f8fafc', borderRadius: 10, padding: 14,
          border: '1.5px solid #e4e7ec',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <Tag color="blue" style={{ fontWeight: 600, fontSize: 13 }}>
              {getRegionLabel(diagramType, selectedRegion)}
            </Tag>
            {currentAnn && (
              <Button
                size="small" danger type="text"
                icon={<DeleteOutlined />}
                onClick={() => clearRegion(selectedRegion)}
              >
                Clear
              </Button>
            )}
          </div>

          <Text type="secondary" style={{ fontSize: 11, marginBottom: 6, display: 'block' }}>Status</Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 10 }}>
            {STATUS_OPTIONS.map(opt => {
              const isActive = (currentAnn?.status ?? null) === opt.value ||
                (!currentAnn && opt.value === 'normal');
              return (
                <button
                  key={opt.value}
                  onClick={() => handleAnnotationChange(selectedRegion, { status: opt.value })}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 10px', borderRadius: 7,
                    border: `1.5px solid ${isActive ? opt.color : '#e4e7ec'}`,
                    background: isActive ? opt.bg : '#fff',
                    cursor: 'pointer', textAlign: 'left', width: '100%',
                    transition: 'all 0.12s',
                  }}
                >
                  <span style={{
                    width: 12, height: 12, borderRadius: 3, flexShrink: 0,
                    background: opt.color,
                    boxShadow: isActive ? `0 0 0 2px ${opt.color}30` : 'none',
                  }} />
                  <span style={{
                    fontSize: 12, fontWeight: isActive ? 600 : 400,
                    color: isActive ? opt.color : '#667085',
                  }}>
                    {opt.label}
                  </span>
                  {isActive && (
                    <CheckCircleOutlined style={{ marginLeft: 'auto', color: opt.color, fontSize: 12 }} />
                  )}
                </button>
              );
            })}
          </div>

          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
            Note (optional)
          </Text>
          <Input.TextArea
            value={currentAnn?.note ?? ''}
            onChange={e => handleAnnotationChange(selectedRegion, { note: e.target.value })}
            placeholder="e.g., cavity, pain on pressure, inflammation…"
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{ borderRadius: 7 }}
            disabled={!currentAnn || currentAnn.status === 'normal'}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <Button
              size="small" block
              style={{ borderRadius: 6 }}
              onClick={() => setSelectedRegion(null)}
            >
              Done
            </Button>
          </div>
        </div>
      ) : (
        <div style={{
          background: '#f8fafc', borderRadius: 10, padding: 14,
          border: '1.5px dashed #d0d5dd', textAlign: 'center',
        }}>
          <AimOutlined style={{ fontSize: 24, color: '#d0d5dd', marginBottom: 8 }} />
          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            {isDental
              ? 'Click a tooth to annotate it'
              : 'Click a body region to annotate it'}
          </Text>
        </div>
      )}

      {/* Annotated regions summary */}
      {annotatedCount > 0 && (
        <>
          <Divider style={{ margin: '0' }} />
          <div>
            <Text style={{ fontSize: 12, fontWeight: 600, color: '#344054' }}>
              Annotated ({annotatedCount})
            </Text>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
              {Object.entries(annotations).map(([id, ann]) => {
                const statusOpt = STATUS_OPTIONS.find(o => o.value === ann.status);
                return (
                  <div
                    key={id}
                    onClick={() => setSelectedRegion(id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '5px 8px', borderRadius: 7,
                      background: selectedRegion === id ? statusOpt?.bg ?? '#fff' : '#fff',
                      border: `1px solid ${selectedRegion === id ? (statusOpt?.color ?? '#d0d5dd') : '#e4e7ec'}`,
                      cursor: 'pointer',
                      transition: 'all 0.12s',
                    }}
                  >
                    <span style={{
                      width: 8, height: 8, borderRadius: 2, flexShrink: 0,
                      background: statusOpt?.color ?? '#d0d5dd',
                    }} />
                    <span style={{ fontSize: 11, fontWeight: 500, color: '#344054', flex: 1, minWidth: 0 }}>
                      {getRegionLabel(diagramType, id)}
                    </span>
                    {ann.note && (
                      <span style={{ fontSize: 10, color: '#98a2b3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 80 }}>
                        {ann.note}
                      </span>
                    )}
                    <Button
                      size="small" type="text" danger
                      icon={<CloseCircleOutlined />}
                      style={{ flexShrink: 0, padding: 0, width: 20, height: 20, fontSize: 12 }}
                      onClick={e => { e.stopPropagation(); clearRegion(id); }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={
        <Space>
          <AimOutlined />
          <span>{isDental ? '🦷 Dental Chart' : '🫀 Body Map'}</span>
          {annotatedCount > 0 && (
            <Badge
              count={annotatedCount}
              style={{ backgroundColor: '#1677ff' }}
              title={`${annotatedCount} region(s) annotated`}
            />
          )}
        </Space>
      }
      width={isDental ? 1040 : 620}
      style={{ top: 20 }}
      footer={
        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {annotatedCount > 0
              ? `${annotatedCount} region${annotatedCount > 1 ? 's' : ''} annotated`
              : 'No annotations yet'}
          </Text>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
          >
            Save Diagram
          </Button>
        </Space>
      }
      destroyOnClose
    >
      <Spin spinning={saving}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          {/* Chart area */}
          <div style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
            {isDental
              ? <DentalChart annotations={annotations} onRegionClick={handleRegionClick} />
              : <BodyMap annotations={annotations} onRegionClick={handleRegionClick} />
            }
          </div>

          {/* Annotation panel */}
          {annotationPanel}
        </div>
      </Spin>
    </Modal>
  );
};

export default DiagramViewer;
